import { ChampsOrigine } from '../tracabilite/ChampsOrigine';
import { garantirTracabiliteReception } from '../../lib/tracabilite';
import { useState } from 'react';
import { X, ClipboardList } from '../../lib/icons';
import { toast } from 'sonner';
import { updateReception, ventilerReception, nomTiers, decompte, QUALITES, MODES_ACHAT, DEVENIRS } from '../../lib/dataProvider';
import { sansPerte } from '../../lib/conflitEcriture';
import { figerCours } from '../../lib/cours';
import { tarifTriturationPour } from '../../lib/tarifs';
import { Plus, Trash2 } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';

const familleDe = (qualite) => (QUALITES.find((q) => q.id === qualite)?.famille === 'lampante' ? 'lampante' : 'noble');

const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

// Étape 2 : compléter les détails de la transaction sans bloquer la bascule.
export const CompleterBonDialog = ({ reception: r, onClose, onDone }) => {
  const { user } = useAuth();
  const client = r.parcours === 'client';
  const [f, setF] = useState({
    ...garantirTracabiliteReception(r),
    immatriculation: r.immatriculation || '',
    qualite: r.qualite || 'extra_vierge',
    mode: r.mode && r.mode !== 'prestation' ? r.mode : 'foss',
    foss: r.foss ?? '',
    prixFixe: r.prixFixe ?? '',
    prixBaseHuile: r.prixBaseHuile ?? '',
    calamites: r.calamites === true,
    tauxDechet: r.tauxDechet ?? '',
    evaluation: r.evaluation ?? '',
    devenir: r.devenir ?? '',
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  // Conserver le prix déjà attribué à ce bon, même après changement de fiche client.
  // Un ancien bon sans prix garde le repli historique par qualité, sans reprise du tarif client courant.
  const tarifApplique = Number.isFinite(Number(r.tarifTrituration)) && Number(r.tarifTrituration) > 0
    ? Number(r.tarifTrituration) : tarifTriturationPour(f.qualite, null);
  const net = decompte(r).poidsNet;
  const [ventil, setVentil] = useState([]);
  const ventilActive = ventil.length > 0;
  const totalVentil = ventil.reduce((s, l) => s + (Number(l.kg) || 0), 0);
  const resteVentil = Number((net - totalVentil).toFixed(1));
  const ligneVide = (qualite, kg) => ({ qualite, kg: String(kg), mode: 'fixe', prixFixe: '', prixBaseHuile: '', foss: '', tauxDechet: '' });
  const ouvrirVentil = () =>
    setVentil([
      ligneVide(f.qualite === 'lampante' ? 'extra_vierge' : f.qualite, Math.round(net * 0.7)),
      ligneVide('lampante', net - Math.round(net * 0.7)),
    ]);
  const majVentil = (i, k, v) => setVentil((l) => l.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  // Valeur propre à chaque ligne selon son type d'achat et son prix.
  const decompteLigne = (l) =>
    decompte({
      ...r, qualite: l.qualite, poidsBrut: Number(l.kg) || 0, tare: 0, mode: l.mode,
      prixFixe: Number(l.prixFixe) || null, prixBaseHuile: Number(l.prixBaseHuile) || null,
      foss: Number(l.foss) || null, tauxDechet: Number(l.tauxDechet) || 0, calamites: f.calamites,
    });
  const totalVentilTnd = ventil.reduce((s, l) => s + decompteLigne(l).montant, 0);
  const ventilEnAttente = ventil.some((l) => decompteLigne(l).enAttenteRendement);

  const apercu = decompte(
    client
      ? { ...r, tarifTrituration: tarifApplique }
      : { ...r, qualite: f.qualite, mode: f.mode, foss: Number(f.foss) || null, prixFixe: Number(f.prixFixe) || null, prixBaseHuile: Number(f.prixBaseHuile) || null, calamites: f.calamites, tauxDechet: Number(f.tauxDechet) || 0 }
  );

  const enregistrer = async (e) => {
    e.preventDefault();
    if (client) {
      if (!(tarifApplique > 0))
        return toast.error('Aucun prix de trituration paramétré pour cette filière — Administration › Paramètres.');
      if (!f.qualite) return toast.error('Sélectionnez la qualité des olives.');
      const { error: refus } = await sansPerte(() =>
        updateReception(r.id, { ...garantirTracabiliteReception(f), immatriculation: f.immatriculation.trim().toUpperCase(), qualite: f.qualite, tarifTrituration: tarifApplique, devenir: f.devenir || null })
      );
      if (refus) return;
    } else {
      if (!ventilActive) {
        if (f.mode === 'fixe' && !(Number(f.prixFixe) > 0)) return toast.error('Saisissez le prix négocié (TND/kg d’olives).');
        if ((f.mode === 'foss' || f.mode === 'reel') && !(Number(f.prixBaseHuile) > 0)) return toast.error('Saisissez le prix de base de l’huile.');
        if (f.mode === 'foss' && !(Number(f.foss) > 0)) return toast.error('Saisissez le rendement FOSS (%).');
        if (f.mode === 'foss' && (Number(f.tauxDechet) < 0 || Number(f.tauxDechet) >= 100)) return toast.error('Taux de déchet entre 0 et 99 %.');
      }
      if (ventilActive) {
        for (const [i, l] of ventil.entries()) {
          if (l.mode === 'fixe' && !(Number(l.prixFixe) > 0)) return toast.error(`Ligne ${i + 1} : saisissez le prix négocié (TND/kg).`);
          if ((l.mode === 'foss' || l.mode === 'reel') && !(Number(l.prixBaseHuile) > 0)) return toast.error(`Ligne ${i + 1} : saisissez le prix de base de l’huile.`);
          if (l.mode === 'foss' && !(Number(l.foss) > 0)) return toast.error(`Ligne ${i + 1} : saisissez le rendement FOSS (%).`);
        }
        const lignes = ventil.map((l) => ({
          qualite: l.qualite, kg: Number(l.kg) || 0, mode: l.mode,
          prixFixe: l.mode === 'fixe' ? Number(l.prixFixe) : null,
          prixBaseHuile: l.mode === 'foss' || l.mode === 'reel' ? Number(l.prixBaseHuile) : null,
          coursFige: l.mode === 'negocier' ? figerCours(familleDe(l.qualite), user?.name) : null,
          foss: l.mode === 'foss' ? Number(l.foss) : null,
          tauxDechet: l.mode === 'foss' ? Number(l.tauxDechet) || 0 : 0,
        }));
        const { error: refusVentil, valeur: ventilation } = await sansPerte(() =>
          ventilerReception(r.id, lignes, { ...garantirTracabiliteReception(f), immatriculation: f.immatriculation.trim().toUpperCase(), calamites: f.calamites, evaluation: f.evaluation.trim() })
        );
        if (refusVentil) return;
        const { error, lignes: ids } = ventilation;
        if (error) return toast.error(error);
        toast.success(`Bon ${r.id} ventilé en ${ids.length} lignes (${ids.join(', ')}) — total ${fmt(totalVentilTnd, 3)} TND`);
        onDone?.();
        onClose();
        return;
      }
      const { error: refusAchat } = await sansPerte(() =>
        updateReception(r.id, {
          ...garantirTracabiliteReception(f), immatriculation: f.immatriculation.trim().toUpperCase(),
        qualite: f.qualite,
        mode: f.mode,
        foss: f.mode === 'foss' ? Number(f.foss) : null,
        tauxDechet: f.mode === 'foss' ? Number(f.tauxDechet) || 0 : 0,
        evaluation: f.evaluation.trim(),
        prixFixe: f.mode === 'fixe' ? Number(f.prixFixe) : null,
        prixBaseHuile: f.mode === 'foss' || f.mode === 'reel' ? Number(f.prixBaseHuile) : null,
        // « À négocier » : aucun prix de base exigé ici, le cours du jour est figé comme en saisie détaillée.
        coursFige: f.mode === 'negocier' ? r.coursFige ?? figerCours(familleDe(f.qualite), user?.name) : null,
        calamites: f.calamites,
        })
      );
      if (refusAchat) return;
    }
    toast.success(`Bon ${r.id} complété`);
    onDone?.();
    onClose();
  };

  return (
    <div data-testid="completer-overlay" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
      <form onSubmit={enregistrer} data-testid="completer-form" className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <ClipboardList size={18} /> Compléter le bon {r.id}
            </h2>
            <p className="text-xs text-slate-500">{nomTiers(r.tiersId)} · net {fmt(decompte(r).poidsNet)} Kg · {client ? 'trituration client' : 'achat d’olives'}</p>
          </div>
          <button type="button" data-testid="completer-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div><label className={label}>Immatriculation du véhicule</label><input maxLength={32} data-testid="completer-immatriculation" value={f.immatriculation} onChange={set('immatriculation')} className={field} /></div>
          <ChampsOrigine value={f} onChange={setF} />
          {client ? (
            <>
              <div>
                <label className={label}>Qualité des olives (obligatoire — file d’atelier)</label>
                <div className="mt-1.5 grid grid-cols-3 gap-px bg-slate-200">
                  {QUALITES.map((q) => (
                    <button key={q.id} type="button" data-testid={`completer-qualite-client-${q.id}`} onClick={() => setF((p) => ({ ...p, qualite: q.id }))} className={`px-2 py-2 text-xs font-bold ${f.qualite === q.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={label}>Tarif trituration appliqué (TND / kg d’olives)</label>
                <input
                  data-testid="completer-tarif"
                  type="number"
                  step="0.001"
                  min="0"
                  value={tarifApplique}
                  readOnly
                  disabled
                  tabIndex={-1}
                  aria-readonly="true"
                  className={`${field} cursor-not-allowed bg-slate-100 text-slate-600`}
                />
                <p data-testid="completer-tarif-origine" className="mt-1 text-[11px] text-slate-500">
                  Tarif {familleDe(f.qualite) === 'lampante' ? 'LAMPANTE' : 'NOBLE'} imposé par la grille
                  tarifaire (Administration › Paramètres) — non modifiable au pont bascule.
                </p>
              </div>
              <div>
                <label className={label}>Devenir de l’huile</label>
                <select data-testid="completer-devenir" value={f.devenir} onChange={set('devenir')} className={field}>
                  <option value="">À décider à la trituration</option>
                  {DEVENIRS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">La cuve est choisie par l’atelier au moment de la trituration.</p>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between">
                  <label className={label}>{ventilActive ? 'Ventilation par qualité' : 'Qualité des olives'}</label>
                  {!ventilActive && !r.ventile && r.statut !== 'Sur site' && (
                    <button type="button" data-testid="ventiler-ouvrir" onClick={ouvrirVentil} className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.06em] text-slate-700 hover:text-slate-900">
                      <Plus size={12} /> Plusieurs qualités sur ce camion
                    </button>
                  )}
                  {ventilActive && (
                    <button type="button" data-testid="ventiler-annuler" onClick={() => setVentil([])} className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500 hover:text-slate-900">
                      Une seule qualité
                    </button>
                  )}
                </div>
                {ventilActive ? (
                  <div data-testid="ventilation" className="mt-1.5 space-y-2 border-l-2 border-slate-900 bg-slate-50 px-3 py-3">
                    {ventil.map((l, i) => {
                      const d = decompteLigne(l);
                      return (
                      <div key={i} data-testid={`ventil-ligne-${i}`} className="space-y-2 border border-slate-200 bg-white p-2">
                        <div className="grid grid-cols-[1fr_110px_60px_32px] items-center gap-2">
                          <select data-testid={`ventil-qualite-${i}`} value={l.qualite} onChange={(e) => majVentil(i, 'qualite', e.target.value)} className="h-10 border border-slate-300 bg-white px-2 font-data text-sm">
                            {QUALITES.map((q) => <option key={q.id} value={q.id}>{q.label}</option>)}
                          </select>
                          <input data-testid={`ventil-kg-${i}`} type="number" min="0" value={l.kg} onChange={(e) => majVentil(i, 'kg', e.target.value)} placeholder="Kg" className="h-10 border border-slate-300 bg-white px-2 font-data text-sm" />
                          <span data-testid={`ventil-pct-${i}`} className="font-data text-xs text-slate-600">{net > 0 ? `${(((Number(l.kg) || 0) / net) * 100).toFixed(1)} %` : '—'}</span>
                          <button type="button" data-testid={`ventil-suppr-${i}`} onClick={() => setVentil((v) => v.filter((_, j) => j !== i))} className="flex h-8 w-8 items-center justify-center border border-slate-300 text-slate-500 hover:bg-red-600 hover:text-white">
                            <Trash2 size={12} />
                          </button>
                        </div>
                        <div className="grid grid-cols-[1fr_1fr_1fr] gap-2">
                          <select data-testid={`ventil-mode-${i}`} value={l.mode} onChange={(e) => majVentil(i, 'mode', e.target.value)} className="h-9 border border-slate-300 bg-white px-2 font-data text-xs">
                            {MODES_ACHAT.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                          </select>
                          {l.mode === 'fixe' ? (
                            <input data-testid={`ventil-prix-fixe-${i}`} type="number" step="0.001" min="0" value={l.prixFixe} onChange={(e) => majVentil(i, 'prixFixe', e.target.value)} placeholder="TND / kg olives" className="h-9 border border-slate-300 bg-white px-2 font-data text-xs" />
                          ) : (
                            <input data-testid={`ventil-prix-base-${i}`} type="number" step="0.001" min="0" value={l.prixBaseHuile} onChange={(e) => majVentil(i, 'prixBaseHuile', e.target.value)} placeholder="Prix base huile" className="h-9 border border-slate-300 bg-white px-2 font-data text-xs" />
                          )}
                          {l.mode === 'foss' ? (
                            <div className="flex gap-1">
                              <input data-testid={`ventil-foss-${i}`} type="number" step="0.01" min="0" value={l.foss} onChange={(e) => majVentil(i, 'foss', e.target.value)} placeholder="FOSS %" className="h-9 w-1/2 border border-slate-300 bg-white px-2 font-data text-xs" />
                              <input data-testid={`ventil-dechet-${i}`} type="number" step="0.1" min="0" max="99" value={l.tauxDechet} onChange={(e) => majVentil(i, 'tauxDechet', e.target.value)} placeholder="Déchet %" className="h-9 w-1/2 border border-slate-300 bg-white px-2 font-data text-xs" />
                            </div>
                          ) : (
                            <span className="flex h-9 items-center font-data text-[11px] text-slate-500">{l.mode === 'reel' ? 'rendement après trituration' : ''}</span>
                          )}
                        </div>
                        <p data-testid={`ventil-montant-${i}`} className="text-right font-data text-xs font-bold text-slate-900">
                          {d.enAttenteRendement ? 'Montant après rendement réel' : `${fmt(d.montant, 3)} TND`}
                        </p>
                      </div>
                      );
                    })}
                    <div className="flex items-center justify-between">
                      <button type="button" data-testid="ventil-ajouter" onClick={() => setVentil((v) => [...v, ligneVide('vierge', Math.max(0, resteVentil))])} className="flex items-center gap-1 text-xs font-bold text-slate-700 hover:text-slate-900">
                        <Plus size={12} /> Ajouter une ligne
                      </button>
                      <p data-testid="ventil-total" className={`font-data text-xs font-bold ${Math.abs(resteVentil) > 0.5 ? 'text-red-700' : 'text-emerald-700'}`}>
                        Total {fmt(totalVentil)} / {fmt(net)} Kg {Math.abs(resteVentil) > 0.5 ? `(écart ${fmt(resteVentil)})` : '✓'}
                      </p>
                    </div>
                    <p className="text-[11px] text-slate-500">Chaque ligne est un bon autonome ({r.id}, {r.id}/2…) avec son propre type d’achat et son prix — le poids camion reste commun.</p>
                  </div>
                ) : (
                <div className="mt-1.5 grid grid-cols-3 gap-px bg-slate-200">
                  {QUALITES.map((q) => (
                    <button key={q.id} type="button" data-testid={`completer-qualite-${q.id}`} onClick={() => setF((p) => ({ ...p, qualite: q.id }))} className={`px-2 py-2 text-xs font-bold ${f.qualite === q.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>
                      {q.label}
                    </button>
                  ))}
                </div>
                )}
              </div>
              {!ventilActive && (<>
              <div>
                <label className={label}>Type d’achat</label>
                <div className="mt-1.5 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-3">
                  {MODES_ACHAT.map((m) => (
                    <button key={m.id} type="button" data-testid={`completer-mode-${m.id}`} onClick={() => setF((p) => ({ ...p, mode: m.id }))} className={`px-2 py-2 text-left ${f.mode === m.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>
                      <p className="text-xs font-bold">{m.label}</p>
                      <p className={`text-[10px] ${f.mode === m.id ? 'text-slate-300' : 'text-slate-500'}`}>{m.hint}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {f.mode === 'fixe' ? (
                  <div>
                    <label className={label}>Prix négocié (TND / kg d’olives)</label>
                    <input data-testid="completer-prix-fixe" type="number" step="0.001" min="0" value={f.prixFixe} onChange={set('prixFixe')} className={field} />
                  </div>
                ) : (
                  <div>
                    <label className={label}>Prix de base huile (TND / kg)</label>
                    <input data-testid="completer-prix-base" type="number" step="0.001" min="0" value={f.prixBaseHuile} onChange={set('prixBaseHuile')} className={field} />
                  </div>
                )}
                {f.mode === 'foss' && (
                  <div>
                    <label className={label}>Rendement FOSS (%)</label>
                    <input data-testid="completer-foss" type="number" step="0.01" min="0" value={f.foss} onChange={set('foss')} className={field} />
                  </div>
                )}
              </div>
              {f.mode === 'foss' && (
                <div data-testid="completer-fosse-params" className="space-y-3 border-l-2 border-amber-500 bg-amber-50/60 px-3 py-3">
                  <p className="text-xs font-bold uppercase tracking-[0.1em] text-amber-800">Paramètres de la fosse (après déchargement)</p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={label}>Taux de déchet (%)</label>
                      <input data-testid="completer-dechet" type="number" step="0.1" min="0" max="99" value={f.tauxDechet} onChange={set('tauxDechet')} placeholder="feuilles, terre…" className={field} />
                    </div>
                    <div>
                      <label className={label}>Poids utile valorisé</label>
                      <p data-testid="completer-poids-utile" className="tabular mt-1.5 flex h-11 items-center border border-slate-200 bg-slate-100 px-3 font-data text-sm font-semibold text-slate-900">
                        {fmt(apercu.poidsUtile)} Kg
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className={label}>Évaluation / observations fosse</label>
                    <input data-testid="completer-evaluation" value={f.evaluation} onChange={set('evaluation')} placeholder="Ex. : olives propres, 5 % de feuilles, acidité correcte" className={field} />
                  </div>
                </div>
              )}
              </>)}
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" data-testid="completer-calamites" checked={f.calamites} onChange={(e) => setF((p) => ({ ...p, calamites: e.target.checked }))} className="h-4 w-4 accent-slate-900" />
                Déduire le fonds au fournisseur (sinon à charge de l’huilerie)
              </label>
            </>
          )}
          <div data-testid="completer-apercu" className="flex items-center justify-between border-2 border-slate-900 bg-slate-900 px-4 py-3 text-white">
            <span className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-300">
              {ventilActive ? `Total du bon (${ventil.length} lignes)` : apercu.enAttenteRendement ? 'Montant après rendement réel' : client ? 'À facturer au client' : 'À payer au fournisseur'}
            </span>
            <span data-testid="completer-total" className="tabular font-data text-xl font-semibold">
              {ventilActive
                ? `${fmt(totalVentilTnd, 3)} TND${ventilEnAttente ? ' + lignes en attente' : ''}`
                : apercu.enAttenteRendement ? '—' : `${fmt(apercu.montant, 3)} TND`}
            </span>
          </div>
        </div>
        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button type="submit" data-testid="completer-enregistrer" className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white hover:bg-slate-700">Enregistrer</button>
          <button type="button" data-testid="completer-annuler" onClick={onClose} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">Annuler</button>
        </div>
      </form>
    </div>
  );
};
